<template>
  <div class="agenda-layout">
    <AgendaHeader :agendaDataLength="agendaDataLength" :isUserTBPersonnel="isUserTBPersonnel"
      :isSaveDisabled="isSaveDisabled" :isSelectedAgendaToday="isSelectedAgendaToday"
      @edit-save-agenda-data="handleEditSaveAgendaData" @save-editable-table="handleSaveEditableTable"
      @record-session="handleRecordSession" />
    <AgendaTable :sortedAgendaDataWithSerialNumbers="sortedAgendaDataWithSerialNumbers" :isEditMode="isEditMode"
      :isSelectedAgendaToday="isSelectedAgendaToday" :agendaDataLength="agendaDataLength"
      :agendaHighlightedDates="agendaHighlightedDates" :isLoading="isLoading" :editToggle="editToggle"
      :tumorBoardsDropdown="tumorBoardsDropdown" @select-patient="onSelectPatient"
      @cell-edit-complete="onCellEditComplete" />
  </div>
  <div v-if="agendaDataLength">
    <Attendance />
  </div>
</template>

<script setup>
// Copyright (c) 2020-2024 Omics Data Automation, Inc.
// Copyright (c) 2024 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineEmits, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import _ from 'lodash'
import AgendaHeader from '@/ux3/cards/TumorBoardSideBar/AgendaHeader.vue'
import AgendaTable from '@/ux3/cards/TumorBoardSideBar/AgendaTable.vue'
import Attendance from '@/ux3/cards/TumorBoardSideBar/Attendance.vue'
import { search } from '@/components/ui/FindPatientModal/search.js'
import { getHighlightedDates } from '@/ux3/cards/TumorBoardSideBar/getHighlightedDates.js'
import { getPatientDiagnosis } from '@/ux3/cards/TumorBoardSessions/getPatientDiagnosis.js'
import { getTumorBoardOccurrence as getTumorBoardOccurrence } from '@/ux3/cards/TumorBoardSideBar/getTumorBoardOccurrence.js'
import { patchPatientSchedule } from '@/ux3/cards/TumorBoardSideBar/patchPatientSchedule.js'
import { getScheduledPatients } from '@/ux3/cards/TumorBoardSideBar/getScheduledPatients.js'
import { addDefaultDiagnosis, formatDate, formatDateToTBPayload, showToast } from '@/utils/utils.js'
import { sendMailAgenda } from '@/ux3/cards/TumorBoardSideBar/sendMailAgenda.js'
import { getTumorBoardSessionsData } from '@/ux3/cards/TumorBoardSessions/getTumorBoardSessionsData.js'

const emit = defineEmits(['close-agenda', 'form-change', 'request-start-session', 'reset-confirmation'])
const store = useStore()

const sortedAgendaDataWithSerialNumbers = ref([])
const agendaDataLength = ref(0)
const isUserTBPersonnel = ref(false)
const editToggle = ref('')
const tumorBoardsDropdown = ref([])
const patientDiagnosisDropdown = ref([])
const patientDiagnosisData = ref([])
const isSelectedAgendaToday = ref(false)
const modifiedrowData = ref([])
const isSaveDisabled = ref(true)
const patchSaveAgenda = ref('')
const tumorBoardOccurrenceData = ref([])
const agendaHighlightedDates = ref([])
const isLoading = ref(true)

const selectedMeeting = computed(() => store.state.session.meeting)
const upcomingMeetings = computed(() => store.state.session.upcomingMeetings)
const userRoles = computed(() => store.getters.userRoles)
const isEditMode = computed(() => editToggle.value === 'cell')
const agendaData = computed(() => {
  const agendaList = selectedMeeting.value
    && upcomingMeetings.value
    && upcomingMeetings.value.filter((meeting) => meeting[0].calendar_id === selectedMeeting.value.data.calendar_id)
  return agendaList && agendaList.flat() || []
})

onMounted(async () => {
  const tumorBoardOccurrence = await getTumorBoardOccurrence()
  tumorBoardOccurrenceData.value = tumorBoardOccurrence
  tumorBoardsDropdown.value = tumorBoardOccurrenceData.value?.map(item => ({ tb_name: `${item.tb_name}`, tb_id: item.tb_id }))
})

watch(agendaData, async (newAgendaData) => {
  const patientMrnList = newAgendaData.map((item) => item.mrn)
  if (patientMrnList.length > 0) {
    patientDiagnosisData.value = await getPatientDiagnosis(patientMrnList)
  }

  sortedAgendaDataWithSerialNumbers.value = newAgendaData.map((item, index) => {
    return {
      ...item,
      date: formatDate(item.calendar.date),
      serialNumber: index + 1,
      diagnosisArray: getDiagnosisOptions(),
      tb_name: item.calendar.tumor_board.tb_name,
    }
  })

  isLoading.value = false
  const startTime = new Date(newAgendaData[0]?.calendar.date + ' ' + newAgendaData[0]?.calendar.start_time)
  const startTimeUTC = convertDateToUTC(startTime)
  const endTime = new Date(startTime.getTime() + (24 * 60 * 60 * 1000)) // Add 24 hours
  const endTimeUTC = convertDateToUTC(endTime)
  const currentDate = new Date()
  const currentDateUTC = convertDateToUTC(currentDate)
  isSelectedAgendaToday.value = currentDateUTC >= startTimeUTC && currentDateUTC <= endTimeUTC
  isUserTBPersonnel.value = userRoles.value.includes('tb-personnel')
  agendaDataLength.value = sortedAgendaDataWithSerialNumbers.value?.length || 0
  modifiedrowData.value = []
  editToggle.value = ''
  isSaveDisabled.value = true
})

watch(patchSaveAgenda, async (newPatchSaveId) => {
  if (!newPatchSaveId) { return }
  const scheduledPatientFromResponse = await getScheduledPatients()
  store.dispatch('setScheduledPatients', { scheduledPatients: scheduledPatientFromResponse, publish: true })
})

const convertDateToUTC = (date) => {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(),
    date.getUTCMinutes(), date.getUTCSeconds())
}

const getDiagnosisOptions = () => {
  const diagnosisArr = addDefaultDiagnosis(patientDiagnosisData.value)
  patientDiagnosisDropdown.value = diagnosisArr
  return diagnosisArr
}

const setRecordSessionState = async () => {
  const isStartRecordSession = true
  const patientsList = sortedAgendaDataWithSerialNumbers.value.map((element) => ({ ...element, id: element.mrn }))
  const tumorBoardSessionsData = await getTumorBoardSessionsData(patientsList[0].id)
  store.dispatch('setTumorBoardSessionsData', { tumorBoardSessionsData, publish: true })
  store.dispatch('setSessionPatientFromAgenda', { patient: patientsList, publish: true })
  store.dispatch('setRecordSession', { isStartRecordSession, publish: true })
  store.dispatch('setClickedCurrentSession', { isClickedCurrentSession: true, publish: true })
}

const handleRecordSession = () => {
  editToggle.value = ''
  if (isSaveDisabled.value) {
    setRecordSessionState()
    emit('close-agenda')
  } else {
    emit('request-start-session', setRecordSessionState)
  }
}

const onCellEditComplete = (data, newValue, field, oldValue) => {
  if (oldValue === newValue) { return }
  switch (field) {
    case 'notes':
      data[field] = newValue.trim()
      break
    case 'tb_name':
      data[field] = newValue
      break
    case 'diagnosis':
    case 'priority':
      data[field] = typeof newValue === 'string' ? newValue : newValue[field]
      break
    case 'date':
      data[field] = typeof newValue == 'string' ? newValue : formatDate(newValue)
      break
    default:
      data[field] = newValue
      break
  }
  const index = modifiedrowData.value.findIndex((element) => element.schedule_id === data.schedule_id)
  if (index < 0) {
    modifiedrowData.value.push(data)
  } else {
    modifiedrowData.value[index] = data
  }

  const comparisonModifiedData = modifiedrowData.value && modifiedrowData.value.find((element) => element.schedule_id === data.schedule_id)
  const comparisonOriginalAgendaData = agendaData.value && agendaData.value.find((element) => element.schedule_id == data.schedule_id)
  const originalDataWithSerialNumber = { ...comparisonOriginalAgendaData, serialNumber: data.serialNumber }
  if (!_.isEqual(originalDataWithSerialNumber, comparisonModifiedData)) {
    emit('form-change')
    return isSaveDisabled.value = false
  }
  isSaveDisabled.value = true
}

const onSelectPatient = async (event) => {
  if (isEditMode.value) {
    const rowData = event.data
    const agendaHighlightedDatesData = await getHighlightedDates(rowData.calendar.tb_id)
    agendaHighlightedDates.value = agendaHighlightedDatesData
    return
  }
  const rowData = event.data
  // TODO: Handle errors if this call fails.
  const [patient] = await search(rowData.mrn)
  store.dispatch('setRecordSession', { isStartRecordSession: false, publish: true })
  store.dispatch('setClickedCurrentSession', { isClickedCurrentSession: false, publish: true })
  store.dispatch('setSessionPatient', { patient, publish: true })
  editToggle.value = ''
  emit('close-agenda')
}

const handleSaveEditableTable = async () => {
  const data = []
  modifiedrowData.value.map((element) => {
    const payload = {
      schedule_id: element.schedule_id,
      tb_id: getTumorBoardTbId(element?.tb_name || element.calendar.tumor_board.tb_name),
      date: formatDateToTBPayload(element.date || element.calendar.date),
      notes: element.notes,
      priority: element.priority,
      diagnosis: element.diagnosis,
    }
    data.push(payload)
  })

  patchSaveAgenda.value = await patchPatientSchedule(data)
  if (patchSaveAgenda.value.schedule_ids) {
    const sendMailAgendaResponse = await sendMailAgenda(data)
    if (sendMailAgendaResponse.status) { showToast('success', sendMailAgendaResponse.status) }
    else if (sendMailAgendaResponse.detail[0].msg) { showToast('error', 'Error sending mail') }
  }
  isSaveDisabled.value = true
  editToggle.value = ''
  emit('reset-confirmation')
}

const handleEditSaveAgendaData = () => {
  if (isEditMode.value) { return editToggle.value = '' }
  editToggle.value = 'cell'
}

const getTumorBoardTbId = (tumorboardName) => {
  const filteredData = tumorBoardOccurrenceData.value.find(item => item.tb_name === tumorboardName)
  return filteredData?.tb_id || null
}
</script>

<style lang="scss" scoped>
.agenda-layout {
  border: 1px solid var(--Grey200);
  margin: 28px;
  border-radius: 6px;
  background-color: var(--White);
}
</style>
